import { useEffect, useState, useMemo, useContext } from "react";
import { Spinner } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { MenuContext, Icon } from "@lynx/ui-components";

import constants from "lib/constants";
import { TO_SUPPORT_PAGE } from "reducers/types";
import { accountThunks } from "actions-thunks/account-thunks";
import SettingsList from "ui/account-overview/risk-settings/RiskSettingsList";
import { NewRequest } from "./requests/NewRequest";
import AccountEnabledModal from "./modals/AccountEnabledModal";
import { utilityFunctions } from "ui/utilities/utilityFunctions";
import TableComponent from "ui/ui-kit/react-table/TableComponent";
import { requestsThunks } from "actions-thunks/requests-thunks";
import { ModalContext } from "ui/ui-kit/modal/ModalContext";
import { requestTableUtils } from "ui/utilities/requestsTableUtils";
import { isVisibleRequestInOverviewTables } from "ui/utilities/requestVisibility";
import useSocketNamespace from "ui/hooks/useSocket";
import useStatistic from "ui/hooks/useStatistic";
import { supportTableUtils } from "ui/ui-kit/react-table/supportTableUtils";
import { Tooltip } from "react-tooltip";

import "./account-overview.scss";
import RecursiveAccordion from "ui/ui-kit/recursive-accordion/RecursiveAccordion";

const { tableFormattedData, setCellClassName, showInOverlay } =
  requestTableUtils;
const { RequestTypes, mappedAccountSummaryData } = constants;

const AccountOverviewPage = ({ onSideItemClick }) => {
  const { extensions, currentAccount } = useSelector((state) => state.accounts);
  const { isInitializedSocket } = useSelector((state) => state.sockets);
  const { requestsItems, riskSettingMetadata } = useSelector(
    (state) => state.requests
  );
  const { unreadMessages } = useSelector((state) => state.chat);
  const [localExtensions, setLocalExtension] = useState(null);
  const [enabledWindowOpened, setEnabledWindowOpened] = useState(null);
  const { showModal, hideModal } = useContext(ModalContext);
  const dispatch = useDispatch();
  const requestsItemsDataALL = requestsItems?.[RequestTypes.ALL];
  const visibleRequestsItemsDataALL = requestsItemsDataALL?.filter(
    isVisibleRequestInOverviewTables
  );

  const unreadRequestIds = useMemo(() => {
    const ids = new Set();
    unreadMessages?.forEach((m) => {
      if (m?.requestID) {
        ids.add(m.requestID);
      }
    });
    return ids;
  }, [unreadMessages]);

  const getMatchingKey = (searchKey, extensionObject) => {
    const keyMappings = {
      'Volume': ['Volume', 'Qty'],
      'Unrealized': ['Unrealized', 'UnrealizedPL'],
      'Deposits': ['Deposits', 'Deposit']
    };
  
    if (!keyMappings[searchKey]) return searchKey;
  
    return keyMappings[searchKey].find(key => key in extensionObject) || searchKey;
  };

  useSocketNamespace(currentAccount, "applications", isInitializedSocket);
  useStatistic(currentAccount?._id, "Open page Account Overview");

  useEffect(() => {
    if (currentAccount && !!Object.keys(currentAccount).length) {
      dispatch(accountThunks.setAccountExtensionsTC(currentAccount._id));
      dispatch(requestsThunks.getRiskSettingsMetadataTC(currentAccount._id));
      dispatch(
        requestsThunks.getAccountRequestsTC({
          accID: currentAccount._id,
          sort: "updatedAt",
        })
      );
      // show or now show congratulation window for the first time
      setEnabledWindowOpened(!currentAccount.isFirstModalShowed);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentAccount]);

  useEffect(() => {
    if (extensions) {
      setLocalExtension(
        extensions.find((e) => e.code === "summary/balance") || { noData: true }
      );
    }
  }, [extensions]);

  const renderAccountSummary = useMemo(() => {
    if (localExtensions?.noData) {
      return Object.entries(mappedAccountSummaryData).map((ext, index) => {
        return (
          <div key={`${index}-${ext[0]}`} className="this-month-body-item">
            <span>{ext[1]}</span>
            <span className="money-block">N/A</span>
          </div>
        );
      });
    }
    if (localExtensions) {
      const summaryItems = Object.entries(mappedAccountSummaryData).map(
        ([key, label], index) => {
          const matchedKey = getMatchingKey(key, localExtensions.ext);
          const value = localExtensions.ext[matchedKey] ?? 0;
          return (
            <div key={`${index}-${key}`} className="this-month-body-item">
              <span className="money-block">
                {key === "Volume"
                  ? value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  : utilityFunctions.currencyFormatter().format(value)}
              </span>
              <span>{label.toUpperCase()}</span>
            </div>
          );
        }
      );

      // Add Adjusted Net P&L with RecursiveAccordion
      summaryItems.splice(3, 0, 
        <div key="adjusted-net-pl" className="this-month-body-item">
          <RecursiveAccordion {...utilityFunctions.transformExtensionData(localExtensions.ext)} />
        </div>
      );

      return summaryItems;
    }
  }, [localExtensions]);

  const renderNoDataBlock = () => {
    if (
      tableFormattedData(
        true,
        visibleRequestsItemsDataALL,
        riskSettingMetadata,
        currentAccount,
        unreadRequestIds
      ).at(-1) === "no-data"
    ) {
      return (
        <div className="account-overview-no-data-block">
          <div className="account-overview-no-data-block-inner">
            <span>No active requests</span>
            <span>
              see{" "}
              <Link to={TO_SUPPORT_PAGE} className="account-overview-support-link">
                Support Requests
              </Link>
            </span>
          </div>
        </div>
      );
    }
  };

  const cancelRequest = (data) => {
    const id = visibleRequestsItemsDataALL?.find(
      (r) => r.sequenceNumber === data.original.id
    )?._id;
    dispatch(
      requestsThunks.updateRequestStatusTC({
        accID: currentAccount._id,
        requestID: id,
        action: "cancel",
      })
    );
  };

  const showInOverlayHandler = (row, isLastItem) => {
    return showInOverlay({
      row,
      isLastItem,
      showModal,
      hideModal,
      cancelRequest,
      MenuContext,
      requestsItemsDataALL: visibleRequestsItemsDataALL,
    });
  };

  if (localExtensions !== null && currentAccount)
    return (
      <div className="account-overview-wrapper">
        <div className="account-overview-header">
          <div className="account-overview-header-top"></div>
          <span>
            TraderID:&nbsp;
            <span className="semi-bold">
              {currentAccount?.reference?.client || "N/A"}&nbsp;&nbsp; (
              {utilityFunctions.getAccountStatusText(currentAccount?.status)})
            </span>
          </span>
          <span>
            Platform:&nbsp;
            <span className="semi-bold">
              {currentAccount?.software || "N/A"}&nbsp;
              {currentAccount?.softwareType || "N/A"}
            </span>
          </span>
        </div>
        <div className="account-summary-block">
          <h6 className="account-title">
            So far this month
            <>
              <Icon
                className="info-icon"
                onClick={() => onSideItemClick("faq")}
                id="anchor-tooltip-so-far-this-month-title"
                name="info"
              />
              <Tooltip
                delayShow={250}
                delayHide={400}
                anchorSelect="#anchor-tooltip-so-far-this-month-title"
              >
                <span className="so-far-this-month-title-tooltip">
                  Zimtra FAQ
                </span>
              </Tooltip>
            </>
          </h6>
          <div className="account-summary-body">{renderAccountSummary}</div>
        </div>
        <SettingsList currentAccount={currentAccount} />
        {enabledWindowOpened && (
          <AccountEnabledModal
            currentAccount={currentAccount}
            setEnabledWindowOpened={setEnabledWindowOpened}
          />
        )}
        {visibleRequestsItemsDataALL && (
          <div className="account-overview-table">
            <NewRequest
              isActiveRequestsTable
              renderFullListAfterRequest={false}
            />
            <div className="account-overview-table-body">
              <TableComponent
                data={tableFormattedData(
                  true,
                  visibleRequestsItemsDataALL,
                  riskSettingMetadata,
                  currentAccount,
                  unreadRequestIds
                )}
                dateColumns={["created", "updated"]}
                formattingForDateColumns={supportTableUtils.tableTimeFormat}
                showSearch={false}
                showRowOverlay={showInOverlayHandler}
                setCellClassName={setCellClassName}
                itemsToUseSorting={[
                  "id",
                  "category",
                  "type",
                  "state",
                  "updated",
                  "created",
                ]}
              />
              {renderNoDataBlock()}
            </div>
          </div>
        )}
      </div>
    );

  return (
    <div className="account-setup-wrapper">
      <Spinner className="spinner" animation="grow" />
    </div>
  );
};

export default AccountOverviewPage;
